<script lang="ts" setup>
import type { HTMLAttributes } from 'vue'

import { cn } from 'cn'

import type { Language } from '~/types'

import { mapToApiLanguage } from '~/utils/api/language'
import { queryKeys as configQueryKeys } from '~/composables/api/config/useQueryConfig'

interface LocaleOption {
  code: Language
  text: string
}

const props = defineProps<{
  class?: HTMLAttributes['class']
  iconOnly?: boolean
}>()

const { t } = useI18n()
const { user } = useAuth()
const { language } = useLanguage()
const { sortIntl } = useIntl()
const queryClient = useQueryClient()

const locales = computed<LocaleOption[]>(() => [
  {
    code: 'de',
    text: 'Deutsch',
  },
  {
    code: 'en-GB',
    text: 'English',
  },
  {
    code: 'fi',
    text: 'Suomi',
  },
  {
    code: 'sv',
    text: 'Svenska',
  },
  {
    code: 'pl',
    text: 'Polski',
  },
])

const sortedLocales = computed(() => sortIntl(locales.value, ({ text }) => text))

const setLanguage = async (code: Language) => {
  language.value = code

  if (user.value) {
    const res = await useApi().updateUser({
      id: user.value.id,
      type: user.value.type,
      attributes: {
        language: mapToApiLanguage(language.value),
      },
    })

    user.value = res.data
    queryClient.invalidateQueries({ queryKey: configQueryKeys._def })
  }
}

const currentLocale = computed(() =>
  locales.value.find(({ code }) => code === language.value),
)

// The tooltip provides its own popper context, so the icon trigger registers as the tooltip's anchor
// instead of the select's. The select content needs the trigger as an explicit reference.
const iconTrigger = useTemplateRef('iconTrigger')
const iconTriggerElement = computed(() => unrefElement(iconTrigger) ?? undefined)
</script>

<template>
  <UiSelect
    :model-value="currentLocale?.code ?? 'en-GB'"
    @update:model-value="(code) => setLanguage(code as Language)"
  >
    <UiTooltip v-if="iconOnly">
      <UiTooltipTrigger as-child>
        <UiSelectTrigger
          ref="iconTrigger"
          :aria-label="t('appLocaleSelect.label')"
          :class="cn('flex size-9 items-center justify-center border-0 p-0 hover:bg-gray-100', props.class)"
          omit-icon
        >
          <UiSelectValue>
            <UiIcon
              name="docue:globe-01"
              size="1.25rem"
            />
            <span
              class="sr-only"
              :lang="currentLocale?.code"
            >
              {{ currentLocale?.text }}
            </span>
          </UiSelectValue>
        </UiSelectTrigger>
      </UiTooltipTrigger>
      <UiTooltipContent>
        {{ t('appLocaleSelect.label') }}
      </UiTooltipContent>
    </UiTooltip>

    <UiSelectTrigger
      v-else
      :aria-label="t('appLocaleSelect.label')"
      :class="cn('border-0', props.class)"
    >
      <UiSelectValue>
        <div class="flex items-center gap-2">
          <UiIcon
            name="docue:globe-01"
            size="1rem"
          />
          <span :lang="currentLocale?.code">
            {{ currentLocale?.text }}
          </span>
        </div>
      </UiSelectValue>
    </UiSelectTrigger>
    <UiSelectContent
      align="center"
      :reference="iconTriggerElement"
    >
      <UiSelectGroup>
        <UiSelectItem
          v-for="locale in sortedLocales"
          :key="locale.code"
          :value="locale.code"
          :lang="locale.code"
        >
          {{ locale.text }}
        </UiSelectItem>
      </UiSelectGroup>
    </UiSelectContent>
  </UiSelect>
</template>
